import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { exposeDebugHook } from '@/app/debug';
import { bootServices, type BootResult, type GameServices } from '@/app/services';
import type { Game } from '@/game/Game';
import { BagTray } from '@/ui/components/BagTray';
import { GameCanvas, loadGame, type GameStatus } from '@/ui/components/GameCanvas';
import { Hud } from '@/ui/components/Hud';
import { PlacementBar } from '@/ui/components/PlacementBar';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { ErrorScreen } from '@/ui/screens/ErrorScreen';
import { SaveRecoveryScreen } from '@/ui/screens/SaveRecoveryScreen';
import { UnlocksScreen } from '@/ui/screens/UnlocksScreen';
import { OtherTabScreen } from '@/ui/screens/OtherTabScreen';
import { MiniGameOverlay } from '@/ui/screens/MiniGameOverlay';
import { MiniGameEntry } from '@/ui/components/MiniGameEntry';
import { ShopBarks } from '@/ui/components/ShopBarks';
import { UpdatePrompt } from '@/ui/components/UpdatePrompt';
import { NativeUpdatePrompt } from '@/ui/components/NativeUpdatePrompt';
import { InstallHint } from '@/ui/components/InstallHint';
import { ToastHost } from '@/ui/components/ToastHost';
import { UndoBar } from '@/ui/components/UndoBar';
import { UiErrorBoundary } from '@/ui/components/UiErrorBoundary';
import { SelectionCard } from '@/ui/components/SelectionCard';
import { CoinFly } from '@/ui/components/CoinFly';
import { ScreenSkeleton } from '@/ui/components/ScreenSkeleton';
import { NavBar, ShopActions, StudioActions, type Panel } from '@/ui/components/NavButtons';
import { CatalogSheet } from '@/ui/screens/CatalogSheet';
import { StoryHost } from '@/ui/screens/StoryHost';
import { TasksSheet } from '@/ui/screens/TasksSheet';
import { OrdersSheet } from '@/ui/screens/OrdersSheet';
import { StudioSheet } from '@/ui/screens/StudioSheet';
import { ShootBrief, ShootResult, ShutterFlash } from '@/ui/screens/StudioShoot';
import { MenuSheet } from '@/ui/screens/MenuSheet';
import { MailboxSheet } from '@/ui/screens/MailboxSheet';
import { BossScreen } from '@/ui/screens/BossScreen';
import { MODAL_UNLOCKS, UnlockModal } from '@/ui/screens/UnlockModal';
import { ExpansionModal } from '@/ui/screens/ExpansionModal';
import sheetStyles from '@/ui/screens/CatalogSheet.module.css';
import { fromSavedPanel, toSavedPanel, type CatalogTab } from './resumePanel';
import { useShortcuts } from './useShortcuts';
import { CoachSpotlight } from '@/ui/components/CoachSpotlight';
import { KeyboardInset } from '@/ui/layouts/KeyboardInset';
import type { DialogueId, UnlockId } from '@/core/content/ids';
import { DialogueOverlay } from '@/ui/screens/DialogueOverlay';
import { useSave } from '@/ui/services/ServicesContext';
import { TaskCoach } from '@/ui/components/TaskCoach';
import { LoadingScreen } from '@/ui/screens/LoadingScreen';
import { ExitConfirm } from '@/ui/screens/ExitConfirm';
import { attachBrowserHistory, runBack } from '@/ui/back/backStack';
import { attachFocusTrap } from '@/ui/back/focusTrap';
import { ServicesProvider } from '@/ui/services/ServicesContext';
import styles from './AppShell.module.css';

/**
 * Screens opened from the menu are separate chunks (M4-13): boot only loads what the first frame needs.
 * They are fetched in the background once the room is up, so opening one never waits.
 */
const loadSettingsScreen = () => import('@/ui/screens/SettingsScreen');
const loadCreditsScreen = () => import('@/ui/screens/CreditsScreen');
const loadAchievementsScreen = () => import('@/ui/screens/AchievementsScreen');
const loadAlbumScreen = () => import('@/ui/screens/AlbumScreen');
const loadTownMap = () => import('@/ui/screens/TownMap');
const loadPhotoMode = () => import('@/ui/screens/PhotoMode');
const loadProfileScreen = () => import('@/ui/screens/ProfileScreen');
const loadShortcutsSheet = () => import('@/ui/screens/ShortcutsSheet');
const SettingsScreen = lazy(() => loadSettingsScreen().then((m) => ({ default: m.SettingsScreen })));
const CreditsScreen = lazy(() => loadCreditsScreen().then((m) => ({ default: m.CreditsScreen })));
const AchievementsScreen = lazy(() =>
  loadAchievementsScreen().then((m) => ({ default: m.AchievementsScreen })),
);
const AlbumScreen = lazy(() => loadAlbumScreen().then((m) => ({ default: m.AlbumScreen })));
const TownMap = lazy(() => loadTownMap().then((m) => ({ default: m.TownMap })));
const PhotoMode = lazy(() => loadPhotoMode().then((m) => ({ default: m.PhotoMode })));
const ProfileScreen = lazy(() => loadProfileScreen().then((m) => ({ default: m.ProfileScreen })));
const ShortcutsSheet = lazy(() => loadShortcutsSheet().then((m) => ({ default: m.ShortcutsSheet })));
const LAZY_SCREENS = [
  loadSettingsScreen,
  loadCreditsScreen,
  loadAchievementsScreen,
  loadAlbumScreen,
  loadTownMap,
  loadPhotoMode,
  loadProfileScreen,
  loadShortcutsSheet,
];

/** Boot first (save + content), then mount the game. Boot problems never reset the save. */
export function AppShell() {
  const [boot, setBoot] = useState<BootResult | { kind: 'booting' } | { kind: 'failed' }>({
    kind: 'booting',
  });

  useEffect(() => {
    let cancelled = false;
    // Fetch the game chunk while the save boots; GameCanvas awaits the same promise (and reports errors).
    loadGame().catch(() => undefined);
    bootServices().then(
      (result) => {
        if (!cancelled) setBoot(result);
      },
      (error: unknown) => {
        console.error('[boot]', error);
        if (!cancelled) setBoot({ kind: 'failed' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (boot.kind === 'booting') return <LoadingScreen visible />;
  if (boot.kind === 'failed') return <ErrorScreen kind="error" />;
  if (boot.kind === 'corrupt') return <SaveRecoveryScreen rawJson={boot.rawJson} recovery={boot.recovery} />;
  if (boot.kind === 'future') return <ErrorScreen kind="saveFuture" />;
  if (boot.kind === 'otherTab') return <OtherTabScreen />;
  return <GameShell services={boot.services} />;
}

/**
 * Layer stack (ux-screens §2.1): canvas → HUD → sheets/modals → toasts → boot screens.
 * Measured HUD (top) and tray (bottom) heights become camera insets so the room never hides under them.
 */
function GameShell({ services }: { services: GameServices }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<GameStatus>('loading');
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [contextLost, setContextLost] = useState(false);
  const [game, setGame] = useState<Game | null>(null);
  const [panel, setPanel] = useState<Panel>('none');
  const [catalogTab, setCatalogTab] = useState<CatalogTab | undefined>(undefined);
  const [unlocked, setUnlocked] = useState<UnlockId[]>([]);
  const [offer, setOffer] = useState<UnlockId | null>(null);
  const [talking, setTalking] = useState<DialogueId | null>(null);
  /** Tiếng lành level reached by the last "Chụp!", for the result card (studio-nano.md §4.3). */
  const [shootLevelUp, setShootLevelUp] = useState<number | null>(null);
  const [boss, setBoss] = useState(false);
  const [askExit, setAskExit] = useState(false);
  const [tabLost, setTabLost] = useState(false);
  // GameShell renders the ServicesProvider itself, so it reads the store directly (not via useSave).
  const bossEnabled = useStore(services.store, (s) => s.settings.bossButtonEnabled);
  const bossKey = useStore(services.store, (s) => s.settings.bossHotkey);
  // The Studio result card is up: story cards wait until the player says "Xong" (studio-nano.md §4.3).
  const shootResultUp = useStore(
    services.store,
    (s) => s.studio.session?.phase === 'done' && s.lastSession.sceneId === 'studio',
  );
  useEffect(() => {
    if (!bossEnabled || boss) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      if (e.code === bossKey) {
        e.preventDefault();
        setBoss(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [bossEnabled, bossKey, boss]);
  // Another tab took the game over: this one has stopped saving, so it freezes behind the notice.
  useEffect(
    () =>
      services.onSaveOwnershipLost(() => {
        setTabLost(true);
      }),
    [services],
  );
  useEffect(() => {
    if (!tabLost || !game) return;
    game.setPaused('otherTab', true);
    game.miniGames.setPaused(true);
    services.platform.sfx.setMuted(true);
    services.platform.music.setMuted(true);
    services.platform.audio.setSuspended('otherTab', true);
  }, [tabLost, game, services]);
  useEffect(
    () =>
      services.bus.onAny((e) => {
        if (e.type === 'UNLOCK_GRANTED' && MODAL_UNLOCKS.has(e.unlockId))
          setUnlocked((list) => (list.includes(e.unlockId) ? list : [...list, e.unlockId]));
        else if (e.type === 'UNLOCK_OFFERED') setOffer(e.unlockId);
      }),
    [services],
  );
  useEffect(
    () =>
      game?.onSceneRequest((r) => {
        game.clearSelection();
        if (r.kind === 'expand') {
          setOffer(r.unlockId);
          return;
        }
        if (r.kind === 'mailbox') {
          setPanel('mail');
          return;
        }
        if (r.kind === 'orders') {
          setPanel('orders');
          return;
        }
        if (r.kind === 'studio') {
          setPanel('studio');
          return;
        }
        // A neighbour visiting Home (content-schemas §14.11).
        const res = services.dispatch({ type: 'talkToNpc', npcId: r.npcId, initiatedBy: 'visit' });
        const talked = res.ok ? res.events.find((e) => e.type === 'NPC_TALKED') : undefined;
        if (talked?.type === 'NPC_TALKED') setTalking(talked.dialogueId);
      }),
    [game, services],
  );
  // Back (ux-screens §6.2): the newest UI layer first, then the scene (drag / selection), and on native
  // the S24 "Tạm nghỉ nhé?" sheet. Web never traps Back: Esc with nothing open simply does nothing.
  useEffect(() => {
    const back = () => {
      if (runBack()) return;
      if (game?.back()) return;
      if (services.platform.app.canExit) setAskExit(true);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Escape') return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (runBack()) e.preventDefault();
      else game?.back();
    };
    window.addEventListener('keydown', onKey);
    const offBack = services.platform.app.onBack(back);
    return () => {
      window.removeEventListener('keydown', onKey);
      offBack();
    };
  }, [game, services]);
  useEffect(() => {
    if (status !== 'loading') services.platform.app.ready();
    // Only a game that really started keeps a live update; an error screen lets native roll it back.
    if (status === 'ready') services.platform.updates.confirm();
  }, [status, services]);
  // Keyboard focus stays inside the top modal; on the web the browser's Back closes the top layer.
  useEffect(() => {
    const offTrap = attachFocusTrap();
    const offHistory = services.platform.app.canExit ? undefined : attachBrowserHistory();
    return () => {
      offTrap();
      offHistory?.();
    };
  }, [services]);

  const hudRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const pushInsets = useCallback(() => {
    const hud = hudRef.current;
    const bottom = bottomRef.current;
    if (!game || !hud) return;
    const layer = hud.parentElement;
    const shellEl = layer?.parentElement;
    const shell = shellEl?.getBoundingClientRect();
    // Toasts sit just under the HUD and the coach card instead of over them.
    if (layer && shellEl && shell)
      shellEl.style.setProperty(
        '--hud-bottom',
        `${String(Math.round(layer.getBoundingClientRect().bottom - shell.top))}px`,
      );
    const top = hud.getBoundingClientRect().bottom - (shell?.top ?? 0);
    const bottomInset = bottom && shell ? shell.bottom - bottom.getBoundingClientRect().top : 0;
    // A docked side panel (tablet/desktop left, landscape phone right) pushes the safe play area aside.
    let left = 0;
    let right = 0;
    // The navigation rail (tablets, landscape phones) takes the left edge.
    const nav = document.querySelector('[data-testid="main-nav"]')?.getBoundingClientRect();
    if (nav && shell && nav.height > shell.height * 0.5) left = nav.right - shell.left;
    const dock = document.querySelector(`.${sheetStyles.sheet ?? 'sheet'}`)?.getBoundingClientRect();
    if (dock && shell && dock.height > shell.height * 0.5) {
      if (dock.left - shell.left < shell.width * 0.25) left = Math.max(left, dock.right - shell.left);
      else if (dock.left - shell.left > shell.width * 0.4) right = shell.right - dock.left;
    }
    game.setInsets({ top, right, bottom: Math.max(0, bottomInset), left });
  }, [game]);

  useEffect(() => {
    const observer = new ResizeObserver(pushInsets);
    if (hudRef.current) observer.observe(hudRef.current);
    if (hudRef.current?.parentElement) observer.observe(hudRef.current.parentElement);
    if (bottomRef.current) observer.observe(bottomRef.current);
    pushInsets();
    return () => {
      observer.disconnect();
    };
  }, [pushInsets]);

  // Panels open and close as docks on wide screens: re-measure once they are laid out.
  useEffect(() => {
    const id = setTimeout(pushInsets, 0);
    return () => {
      clearTimeout(id);
    };
  }, [panel, pushInsets]);

  // Quick resume (ux-screens §7.1): reopen the durable panel from last time once the room is up, then keep
  // `lastSession.panel` in step with what is open. Nothing is written before the restore has run.
  const [resumed, setResumed] = useState(false);
  useEffect(() => {
    if (!resumed) return;
    const next = toSavedPanel(panel, catalogTab);
    const last = services.store.getState().lastSession;
    if (last.panel === next.panel && last.panelParams?.tab === next.panelParams?.tab) return;
    services.dispatch({ type: 'updateLastSession', patch: next });
  }, [resumed, panel, catalogTab, services]);

  const closeAll = useCallback(() => {
    setPanel('none');
    setUnlocked([]);
    setOffer(null);
    setTalking(null);
    setAskExit(false);
  }, []);

  useShortcuts({
    game,
    services,
    panel,
    setPanel,
    paused: boss || unlocked.length > 0 || offer !== null || talking !== null,
  });

  useEffect(() => {
    exposeDebugHook(services, game);
  }, [services, game]);

  const resumeOnce = useRef(false);
  const onStatus = useCallback(
    (next: GameStatus, error?: unknown) => {
      if (error) console.error('[game]', error);
      setStatus(next);
      if (next !== 'ready' || resumeOnce.current) return;
      resumeOnce.current = true;
      const saved = fromSavedPanel(services.store.getState().lastSession);
      if (saved.catalogTab) setCatalogTab(saved.catalogTab);
      if (saved.panel !== 'none') setPanel((open) => (open === 'none' ? saved.panel : open));
      setResumed(true);
    },
    [services],
  );
  // The room is up: fetch the menu screens in the background so none of them waits when opened.
  useEffect(() => {
    if (status !== 'ready') return;
    const timer = setTimeout(() => {
      for (const load of LAZY_SCREENS) load().catch(() => undefined);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [status]);

  return (
    <ServicesProvider services={services} game={game}>
      <div className={styles.shell}>
        <GameCanvas
          services={services}
          onStatus={onStatus}
          onGame={setGame}
          onContextLost={setContextLost}
          onProgress={setProgress}
        />
        <div className={styles.hudLayer} style={panel === 'photo' ? { visibility: 'hidden' } : undefined}>
          <UiErrorBoundary>
            <Hud
              ref={hudRef}
              game={game}
              onRecenter={() => {
                game?.recenter();
              }}
              onMenu={() => {
                game?.clearSelection();
                setPanel('menu');
              }}
              onMail={() => {
                game?.clearSelection();
                setPanel('mail');
              }}
              onExpand={setOffer}
              onAllUnlocks={() => {
                game?.clearSelection();
                setPanel('unlocks');
              }}
              onBoss={
                bossEnabled
                  ? () => {
                      setBoss(true);
                    }
                  : undefined
              }
            />
            {game && (
              <TaskCoach
                game={game}
                onOpen={() => {
                  setPanel('tasks');
                }}
              />
            )}
            {game && status === 'ready' && panel !== 'photo' && <ShootBrief onShot={setShootLevelUp} />}
          </UiErrorBoundary>
        </div>
        <div className={styles.bottomLayer} style={panel === 'photo' ? { visibility: 'hidden' } : undefined}>
          <UiErrorBoundary>
            {game && <PlacementBar game={game} />}
            {game && (
              <div className={styles.actionRow}>
                <UndoBar game={game} />
                <div className={styles.rightActions}>
                  <MiniGameEntry game={game} />
                  <ShopActions
                    game={game}
                    onOpen={(next) => {
                      game.clearSelection();
                      setPanel(next);
                    }}
                  />
                  <StudioActions
                    game={game}
                    onOpen={(next) => {
                      game.clearSelection();
                      setPanel(next);
                    }}
                  />
                </div>
              </div>
            )}
            {/* The selected item's card covers the tray in the same cell; only the tray's height is an inset. */}
            <div className={styles.trayDock}>
              <div ref={bottomRef}>{game && <BagTray game={game} />}</div>
              {game && status === 'ready' && panel !== 'photo' && <SelectionCard game={game} />}
            </div>
          </UiErrorBoundary>
        </div>
        {game && (
          <UiErrorBoundary>
            <NavBar
              game={game}
              panel={panel}
              onOpen={(next) => {
                game.clearSelection();
                setPanel(next);
              }}
              onClose={() => {
                setPanel('none');
              }}
            />
          </UiErrorBoundary>
        )}
        {/* Sheets, modals and toasts: a crash closes what was open instead of blanking the game. */}
        <UiErrorBoundary onReset={closeAll}>
          {game && status === 'ready' && (
            <CoachSpotlight
              game={game}
              paused={unlocked.length > 0 || offer !== null || talking !== null || panel === 'photo'}
            />
          )}
          {game && <MiniGameOverlay game={game} />}
          {game && status === 'ready' && (
            <StoryHost
              game={game}
              blocked={
                panel !== 'none' || unlocked.length > 0 || offer !== null || talking !== null || shootResultUp
              }
            />
          )}
          {(panel === 'catalog' || panel === 'stock') && (
            <CatalogSheet
              initialTab={panel === 'stock' ? 'stock' : (catalogTab ?? 'decor')}
              onTabChange={setCatalogTab}
              onClose={() => {
                setPanel('none');
              }}
              onPlaceNow={() => {
                setPanel('none');
              }}
            />
          )}
          {panel === 'menu' && (
            <MenuSheet
              onClose={() => {
                setPanel('none');
              }}
              onOpen={setPanel}
              {...(bossEnabled
                ? {
                    onBoss: () => {
                      setPanel('none');
                      setBoss(true);
                    },
                  }
                : {})}
            />
          )}
          {/* Only one panel shows at a time; a menu screen's chunk is usually preloaded already. */}
          <Suspense fallback={<ScreenSkeleton />}>
            {panel === 'town' && game && (
              <TownMap
                onClose={() => {
                  setPanel('none');
                }}
                onGoTo={(room) => {
                  setPanel('none');
                  void game.goToRoom(room);
                }}
              />
            )}
            {(panel === 'album' || panel === 'photos') && (
              <AlbumScreen
                initialTab={panel === 'photos' ? 'photos' : 'story'}
                onBack={() => {
                  setPanel('menu');
                }}
              />
            )}
            {panel === 'unlocks' && (
              <UnlocksScreen
                onBack={() => {
                  setPanel('none');
                }}
                onExpand={(id) => {
                  setPanel('none');
                  setOffer(id);
                }}
              />
            )}
            {panel === 'shortcuts' && (
              <ShortcutsSheet
                onClose={() => {
                  setPanel('none');
                }}
              />
            )}
            {panel === 'profile' && (
              <ProfileScreen
                onBack={() => {
                  setPanel('menu');
                }}
              />
            )}
            {panel === 'photo' && game && (
              <PhotoMode
                game={game}
                onClose={() => {
                  setPanel('none');
                }}
                onAlbum={() => {
                  setPanel('photos');
                }}
              />
            )}
            {panel === 'mail' && (
              <MailboxSheet
                onClose={() => {
                  setPanel('none');
                }}
              />
            )}
            {panel === 'settings' && (
              <SettingsScreen
                onBack={() => {
                  setPanel('menu');
                }}
                onCredits={() => {
                  setPanel('credits');
                }}
                onShortcuts={() => {
                  setPanel('shortcuts');
                }}
              />
            )}
            {panel === 'credits' && (
              <CreditsScreen
                onBack={() => {
                  setPanel('menu');
                }}
              />
            )}
            {panel === 'achievements' && (
              <AchievementsScreen
                onBack={() => {
                  setPanel('menu');
                }}
              />
            )}
          </Suspense>
          <TextScale />
          <KeyboardInset />
          {boss && game && (
            <BossScreen
              game={game}
              onBack={() => {
                setBoss(false);
              }}
            />
          )}
          {game && unlocked.length > 0 && (
            <UnlockModal
              unlockIds={unlocked}
              onClose={() => {
                setUnlocked([]);
              }}
              onVisit={(id) => {
                setUnlocked([]);
                if (id === 'unl_minimart' || id === 'unl_npc_orders' || id === 'unl_packing') {
                  void game.goToRoom('minimart');
                  if (id === 'unl_npc_orders') setPanel('orders');
                } else if (id.startsWith('unl_studio')) void game.goToRoom('studio');
              }}
            />
          )}
          {game && offer && unlocked.length === 0 && (
            <ExpansionModal
              unlockId={offer}
              onClose={() => {
                setOffer(null);
              }}
              onExpanded={(id) => {
                setOffer(null);
                const grants = services.content.unlocks.get(id)?.grants ?? [];
                const room = grants.find((g) => g.type === 'roomExpansion');
                // A whole new place (Studio NANO): walk there right away.
                const scene = grants.find((g) => g.type === 'scene');
                if (scene?.type === 'scene' && scene.sceneId !== 'townmap') void game.goToRoom(scene.sceneId);
                else if (room?.type === 'roomExpansion')
                  void game.goToRoom(room.roomId).then(() => {
                    game.recenter();
                  });
              }}
            />
          )}
          {talking && (
            <DialogueOverlay
              dialogueId={talking}
              onDone={() => {
                setTalking(null);
              }}
            />
          )}
          {panel === 'studio' && game && (
            <StudioSheet
              onClose={() => {
                setPanel('none');
              }}
              onStart={() => {
                setPanel('none');
                void game.goToRoom('studio');
              }}
            />
          )}
          {game && status === 'ready' && panel === 'none' && unlocked.length === 0 && offer === null && (
            <ShootResult
              levelUp={shootLevelUp}
              onPhoto={() => {
                setShootLevelUp(null);
                setPanel('photo');
              }}
            />
          )}
          {panel === 'orders' && (
            <OrdersSheet
              onClose={() => {
                setPanel('none');
              }}
              onRestock={() => {
                setCatalogTab('stock');
                setPanel('stock');
              }}
            />
          )}
          {panel === 'tasks' && (
            <TasksSheet
              onClose={() => {
                setPanel('none');
              }}
              onGoTo={(to) => {
                // "Đi tới" (ux-screens §3.8): the room first, then the sheet or mini game intro there.
                setPanel('none');
                if (!game) return;
                void (to.room ? game.goToRoom(to.room) : Promise.resolve(true)).then(() => {
                  if (to.panel === 'stock') setCatalogTab('stock');
                  if (to.panel) setPanel(to.panel);
                  if (to.miniGameId) game.miniGames.openIntro(to.miniGameId);
                });
              }}
              onShowMe={(target) => {
                // "Chỉ chỗ": close the sheet so the coach spotlight can point; go to the room it is in.
                setPanel('none');
                if (game && target?.kind === 'sceneObject' && target.sceneId !== 'townmap')
                  void game.goToRoom(target.sceneId);
              }}
            />
          )}
          {askExit && (
            <ExitConfirm
              onStay={() => {
                setAskExit(false);
              }}
              onLeave={() => {
                void services.saves.flush().finally(() => {
                  services.platform.app.exit();
                });
              }}
            />
          )}
          <ShopBarks />
          <ShutterFlash />
          {game && <CoinFly game={game} />}
          <UpdatePrompt />
          <NativeUpdatePrompt />
          <InstallHint ready={status === 'ready'} />
          <ToastHost ready={status === 'ready'} />
        </UiErrorBoundary>
        {contextLost && (
          <div className={styles.banner} role="status">
            {t('system.context_lost')}
          </div>
        )}
        {tabLost && <OtherTabScreen />}
        <LoadingScreen visible={status === 'loading'} {...(progress === undefined ? {} : { progress })} />
        {(status === 'unsupported' || status === 'error') && <ErrorScreen kind={status} />}
      </div>
    </ServicesProvider>
  );
}

/** Applies the "Cỡ chữ" setting to the root font size (rem-based UI text scales with it). */
function TextScale() {
  const scale = useSave((s) => s.settings.textScale);
  useEffect(() => {
    document.documentElement.style.fontSize = scale === 1 ? '' : `${String(scale * 100)}%`;
  }, [scale]);
  return null;
}
